import { ColumnDef } from '@tanstack/react-table';
import { AvatarNameDescription } from '../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { CopyIcon } from '../../../design-system/copy-icon/copy-icon';
import { ColumnHeaderWithSorting } from '../../../design-system/data-table/column-header-with-sorting';
import { Inline } from '../../../design-system/inline/inline';
import { AccountView } from '../../../types/entity-types';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { getFormattedSortCode } from '../../../utils/get-formatted-sort-code';
import { AccountOptionsButton } from './account-options-button';

export const accountTableColumnsDefinition: ColumnDef<AccountView>[] = [
  {
    id: 'account',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Account" />,
    cell: ({ row }) => {
      const account = row.original;

      const accountName = `${account.institution?.name} (${account.currency})`;
      const logoUrl = account.institution?.logo;

      return (
        <AvatarNameDescription
          avatar={{ showAvatar: true, avatarUrl: logoUrl, size: 'sm' }}
          name={accountName ?? ''}
          // Arbitrary width to make sure the column doesn't take too much space
          // And to avoid there are no jumps in UI when navigating through pages
          // TODO: Tanstack table has a size property, but it doesn't work as expected. Need to investigate further
          // https://github.com/TanStack/table/issues/5115
          className="w-56"
        />
      );
    },
    accessorFn: (row) => (row.institution?.name ? `${row.institution?.name} (${row.currency})` : row.name),
    size: 28,
  },
  {
    id: 'accountNumber',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Account Number / IBAN" />,
    cell: ({ row }) => {
      const { iban, accountNo } = row.original.accountInformation || {};
      const value = accountNo || iban;
      return (
        <Inline className="w-60" space={0}>
          <p className="text-sm">{value}</p>
          {value && <CopyIcon textToCopy={value} iconSize="sm" />}
        </Inline>
      );
    },
    accessorFn: (row) => row.accountInformation?.accountNo || row.accountInformation?.iban,
    size: 27,
  },
  {
    id: 'sortCode',
    accessorKey: 'accountInformation.sortCode',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Sort Code" />,
    cell: ({ row }) => {
      const sortCode = row.original.accountInformation?.sortCode;
      const formattedSortCode = getFormattedSortCode(sortCode);

      return (
        <Inline className="w-28" space={0} wrap="nowrap">
          <p className="text-sm">{formattedSortCode}</p>
          {sortCode && <CopyIcon textToCopy={sortCode} iconSize="sm" />}
        </Inline>
      );
    },
    size: 15,
    minSize: 15,
  },
  {
    id: 'balance',
    accessorKey: 'balance',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Account Balance" />,
    cell: ({ row }) => {
      const { balance, currency } = row.original;

      const balanceWithCurrency = getAmountWithCurrency({ amount: balance, currency, isAccountBalance: true });

      return <p className="text-right text-sm">{balanceWithCurrency}</p>;
    },
    size: 25,
  },
  {
    id: 'options',
    accessorKey: 'options',
    // No header for the ellipsis column
    header: () => null,
    cell: ({ row, table }) => {
      if (!table.options.meta?.showAccountOptions) {
        // Show an empty div to align the column with the other columns
        return <div className="w-8" />;
      }
      const onDisconnectAccount = table.options.meta?.onDisconnectAccount;
      return (
        <AccountOptionsButton
          onDisconnectAccount={async () => {
            await onDisconnectAccount?.(row.original.id);
          }}
        />
      );
    },
    size: 5,
    minSize: 5,
  },
];
